"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useOptimistic,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** How an in-flight add resolved. Decides whether a parked cart navigation
 *  is performed (`success`) or dropped (`error`). */
export type AddOutcome = "success" | "error";

// Safety valve for a parked navigation whose add never resolves (the tab was
// backgrounded mid-flight, the action died without settling). Long enough
// that a normal add — 1.4-4.4s in production — always wins the race, short
// enough that the cart link is never permanently dead.
const PARKED_NAVIGATION_TIMEOUT_MS = 10_000;

// Safety valve for the navigation-in-flight flag below: a full load that
// never unloads this document (Esc mid-load, a dropped connection) would
// otherwise leave every cart link disabled forever. Generous, because the
// flag resolves by the page disappearing — firing early just re-enables a
// link the buyer is no longer looking at.
const CART_NAVIGATION_TIMEOUT_MS = 15_000;

// The same valve for the hand-off to Shopify checkout, which is a different
// wait: cross-origin, and not a page we can make faster. The click evidence
// behind task 173 shows buyers sitting through 45s of it. A valve that fires
// mid-wait un-dims the button and invites exactly the extra tap the pending
// state exists to prevent, so this one is deliberately long — past the point
// where anyone is still watching, and only there to stop a permanently dead
// button if the load dies without unloading this document.
export const CHECKOUT_NAVIGATION_TIMEOUT_MS = 60_000;

interface CartCountContextValue {
  count: number;
  /** Server-known total, before any optimistic delta. */
  baseCount: number;
  /**
   * The count to *show*. While an add is in flight the optimistic delta is
   * withheld — the header renders a spinner instead of a number, and the
   * "view cart" buttons stay hidden — so nothing claims the line landed
   * before the server said so. A cart that was already non-empty keeps its
   * number and its buttons throughout.
   */
  settledCount: number;
  /**
   * What the header badge shows: `settledCount`, unless the cart page has
   * line edits Shopify hasn't confirmed yet, in which case it's the total
   * those edits add up to. Kept apart from `settledCount` on purpose —
   * readers of that (the phone-sticker gate re-quotes on every change)
   * must only ever see figures Shopify has confirmed.
   */
  displayCount: number;
  /**
   * Set by the cart page while its line edits are unconfirmed; `null` hands
   * the badge back to `settledCount`.
   */
  setPendingCartCount: (count: number | null) => void;
  /** True from the moment an add starts until the server action settles. */
  isAddInFlight: boolean;
  addOptimistic: (delta: number) => void;
  setBaseCount: (count: number) => void;
  /** True while a cart navigation is parked, waiting on an in-flight add. */
  isCartNavigationParked: boolean;
  /**
   * Open the window during which cart navigations are parked and the header
   * shows a spinner. Call this *outside* the submitting transition — see the
   * timing note on `isAddInFlight` in the provider.
   */
  beginAdd: () => void;
  /**
   * Close that window. Returns true when a parked navigation was performed,
   * so the caller can skip its own redirect instead of firing two loads.
   * Idempotent — calling it without an open window is a no-op.
   */
  endAdd: (outcome: AddOutcome) => boolean;
  /**
   * Park a cart navigation when an add is in flight. Returns true when
   * parked, in which case the caller MUST `preventDefault()`.
   */
  parkCartNavigation: (href: string) => boolean;
  /**
   * True from the moment a full document load to the cart begins until the
   * document actually unloads (or the safety valve fires). The cart page is
   * dynamic and per-user, so the load takes a beat — cart links render a
   * pending state off this so the click visibly landed.
   */
  isCartNavigationInFlight: boolean;
  /**
   * Open the navigation-in-flight window. Called right before the browser
   * is handed the navigation (link default or `window.location.href`).
   * `timeoutMs` overrides the safety valve for destinations with a different
   * wait profile — see `CHECKOUT_NAVIGATION_TIMEOUT_MS`.
   */
  beginCartNavigation: (timeoutMs?: number) => void;
}

const CartCountContext = createContext<CartCountContextValue | null>(null);

interface CartCountProviderProps {
  initialCount?: number;
  children: ReactNode;
}

export function CartCountProvider({ initialCount = 0, children }: CartCountProviderProps) {
  const [baseCount, setBaseCount] = useState(initialCount);
  const [pendingCartCount, setPendingCartCount] = useState<number | null>(null);
  const [count, addOptimistic] = useOptimistic(baseCount, (state, delta: number) =>
    Math.max(0, state + delta),
  );

  // Add-in-flight bookkeeping. A full document load while the add-to-cart
  // server action is still on the wire aborts that POST — Safari reports it
  // as `TypeError: Load failed` and the line is silently never added. The
  // optimistic badge above is what invites that tap: it flips to 1
  // immediately, which reveals the "view cart" buttons in the configurator
  // and puts a filled cart icon in the header, ~2s before the action
  // resolves. So rather than dropping the optimistic feedback (or ignoring
  // the tap), we honour the tap late: park the navigation and perform it
  // from `endAdd` once the line has actually committed.
  //
  // Refs, not state, for the two values read inside callbacks: a click
  // handler must see the live in-flight flag, and `endAdd` runs inside the
  // configurator's async transition where a stale closure would misfire.
  const addInFlightRef = useRef(false);
  const parkedHrefRef = useRef<string | null>(null);
  const parkedTimerRef = useRef<number | null>(null);
  const [isCartNavigationParked, setIsCartNavigationParked] = useState(false);
  // Mirrors `addInFlightRef` for rendering — the header spinner and the "view
  // cart" buttons subscribe to this; the ref stays the source of truth for the
  // callbacks below, which need the live value. Note the asymmetry in timing:
  // `beginAdd` must be called outside the caller's transition to render at all
  // (React defers plain state updates made inside an async transition), while
  // `endAdd` runs inside it — so the spinner clears on the same commit that
  // drops the optimistic count and lands the server's new total.
  const [isAddInFlight, setIsAddInFlight] = useState(false);

  const clearParkedTimer = useCallback(() => {
    if (parkedTimerRef.current != null) {
      window.clearTimeout(parkedTimerRef.current);
      parkedTimerRef.current = null;
    }
  }, []);

  // Navigation-in-flight bookkeeping. The flag normally resolves by this
  // document unloading, so there is no "end" callback — only the safety
  // valve and the bfcache restore below ever clear it.
  const [isCartNavigationInFlight, setIsCartNavigationInFlight] = useState(false);
  const navigationTimerRef = useRef<number | null>(null);

  const clearCartNavigation = useCallback(() => {
    if (navigationTimerRef.current != null) {
      window.clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
    setIsCartNavigationInFlight(false);
  }, []);

  const beginCartNavigation = useCallback((timeoutMs = CART_NAVIGATION_TIMEOUT_MS) => {
    if (navigationTimerRef.current != null) {
      window.clearTimeout(navigationTimerRef.current);
    }
    setIsCartNavigationInFlight(true);
    navigationTimerRef.current = window.setTimeout(() => {
      navigationTimerRef.current = null;
      setIsCartNavigationInFlight(false);
    }, timeoutMs);
  }, []);

  // Back/forward from the cart restores this page from the bfcache with its
  // JS state intact — including a stale "navigating" flag that would leave
  // every cart link disabled. `pageshow` with `persisted` is that restore.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        clearCartNavigation();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      clearCartNavigation();
    };
  }, [clearCartNavigation]);

  const beginAdd = useCallback(() => {
    addInFlightRef.current = true;
    parkedHrefRef.current = null;
    clearParkedTimer();
    setIsCartNavigationParked(false);
    setIsAddInFlight(true);
  }, [clearParkedTimer]);

  const endAdd = useCallback(
    (outcome: AddOutcome) => {
      if (!addInFlightRef.current) {
        return false;
      }
      addInFlightRef.current = false;
      clearParkedTimer();
      setIsAddInFlight(false);

      const parkedHref = parkedHrefRef.current;
      parkedHrefRef.current = null;
      setIsCartNavigationParked(false);

      // On failure the parked navigation is dropped: the user stays put and
      // sees the error notice instead of landing on a cart missing the line
      // they thought they added. `useOptimistic` reverts the badge on its
      // own when the transition ends.
      if (outcome !== "success" || !parkedHref) {
        return false;
      }

      beginCartNavigation();
      window.location.href = parkedHref;
      return true;
    },
    [beginCartNavigation, clearParkedTimer],
  );

  const parkCartNavigation = useCallback((href: string) => {
    if (!addInFlightRef.current) {
      return false;
    }
    parkedHrefRef.current = href;
    setIsCartNavigationParked(true);
    parkedTimerRef.current = window.setTimeout(() => {
      parkedTimerRef.current = null;
      const pendingHref = parkedHrefRef.current;
      if (pendingHref) {
        parkedHrefRef.current = null;
        beginCartNavigation();
        window.location.href = pendingHref;
      }
    }, PARKED_NAVIGATION_TIMEOUT_MS);
    return true;
  }, [beginCartNavigation]);

  useEffect(() => clearParkedTimer, [clearParkedTimer]);

  const settledCount = isAddInFlight ? baseCount : count;

  return (
    <CartCountContext.Provider
      value={{
        count,
        baseCount,
        settledCount,
        displayCount: pendingCartCount ?? settledCount,
        setPendingCartCount,
        isAddInFlight,
        addOptimistic,
        setBaseCount,
        isCartNavigationParked,
        beginAdd,
        endAdd,
        parkCartNavigation,
        isCartNavigationInFlight,
        beginCartNavigation,
      }}
    >
      {children}
    </CartCountContext.Provider>
  );
}

export function useCartCount(): CartCountContextValue {
  const ctx = useContext(CartCountContext);
  if (!ctx) {
    return {
      count: 0,
      baseCount: 0,
      settledCount: 0,
      displayCount: 0,
      setPendingCartCount: () => {},
      isAddInFlight: false,
      addOptimistic: () => {},
      setBaseCount: () => {},
      isCartNavigationParked: false,
      beginAdd: () => {},
      endAdd: () => false,
      parkCartNavigation: () => false,
      isCartNavigationInFlight: false,
      beginCartNavigation: () => {},
    };
  }
  return ctx;
}

export function CartCountHydrator({ count }: { count: number }) {
  const { setBaseCount } = useCartCount();
  useEffect(() => {
    setBaseCount(count);
  }, [count, setBaseCount]);
  return null;
}
